/**
 * The HTTP status of an error, whatever shape it arrived in.
 *
 * An error reaching the auth layer may be an `HttpErrorResponse` (`status`), an {@link ApiError}
 * (`statusCode`), or a plain `Error` from a network failure (neither). Classification code must not
 * have to know which, because getting it wrong fails silently: a missing field reads as `undefined`,
 * every comparison is false, and the branch that should have refreshed the token simply never runs.
 */
export function httpStatusOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as { status?: unknown; statusCode?: unknown };
  const status = typeof candidate.status === 'number' ? candidate.status : undefined;
  const statusCode = typeof candidate.statusCode === 'number' ? candidate.statusCode : undefined;
  return status ?? statusCode;
}

/**
 * True when the response body says the session itself has ended, in any of the shapes the API
 * and the two error-reshaping layers can deliver it.
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const candidate = error as { sessionExpired?: unknown; error?: { sessionExpired?: unknown } };
  return candidate.sessionExpired === true || candidate.error?.sessionExpired === true;
}

/**
 * The API's reason code for an auth refusal (`idle_expired`, `no_session`, …), when present.
 */
export function authReasonOf(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as { reason?: unknown; error?: { reason?: unknown } };
  const reason = typeof candidate.reason === 'string' ? candidate.reason : undefined;
  const nested = typeof candidate.error?.reason === 'string' ? candidate.error.reason : undefined;
  return reason ?? nested;
}

/**
 * Whether a failed auth call is the end of the session or a passing problem.
 *
 * Terminal: the server rejected the credential (401/403), or said the session is over.
 * Temporary: no status at all (network), 408, 429, or any 5xx — including the 503 the API now
 * answers when it cannot read session state. Treating those as terminal is what turned a database
 * blip into a forced logout.
 */
export function isTerminalAuthFailure(error: unknown): boolean {
  if (isSessionExpiredError(error)) {
    return true;
  }
  const status = httpStatusOf(error);
  return status === 401 || status === 403;
}

/**
 * When the server told us how long to wait — `Retry-After` (seconds or HTTP date) or a
 * rate-limit reset timestamp — the moment it is willing to be asked again, in epoch milliseconds.
 */
export function serverRetryAtOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as {
    headers?: { get?: (name: string) => string | null };
    rateLimitResetAt?: unknown;
    error?: { rateLimitResetAt?: unknown };
  };

  const retryAfter = candidate.headers?.get?.('Retry-After');
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Date.now() + seconds * 1000;
    }
    const asDate = Date.parse(retryAfter);
    if (!Number.isNaN(asDate)) {
      return asDate;
    }
  }

  const resetAt = candidate.rateLimitResetAt ?? candidate.error?.rateLimitResetAt;
  if (typeof resetAt === 'string') {
    const parsed = Date.parse(resetAt);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return undefined;
}
